import { PaginationType } from "@/http/types";
import { FirstWeekContainsDate, format, Locale, parseISO } from "date-fns";

export const valueExists = (value: any) => {
  if (!["number", "string"].includes(typeof value)) return false;

  return (
    value !== null &&
    typeof value !== "undefined" &&
    String(value).trim() !== ""
  );
};

export function setPaginationParams(url: string, pagination: PaginationType) {
  const newUrl = new URL(url);

  for (const key in pagination) {
    if (pagination[key as keyof PaginationType])
      newUrl.searchParams.set(
        key,
        String(pagination[key as keyof PaginationType]).toString()
      );
  }

  return newUrl.toString();
}

export function getDateOfDateTime(date: string) {
  const dateNoTimeZone = date.split("T")[0].replaceAll("-", "/");

  return format(dateNoTimeZone, "yyyy-MM-dd");
}

export function getDateInputValue(date: string) {
  const dateNoTimeZone = date.split("T")[0].replaceAll("-", "/");

  return format(dateNoTimeZone, "dd/MM/yyyy");
}

export function getTimeOfDateTime(date: string) {
  return format(parseISO(date), "HH:mm");
}

export function getFormattedAddress(address: any) {
  const addressAttributes = [
    address?.street,
    address?.neighborhood,
    address?.number,
    address?.city
  ].filter(Boolean);

  return addressAttributes.join(", ");
}

export function capitalizeFirstLetter(word: string) {
  return `${word?.charAt(0).toUpperCase()}${word?.slice(1).toLowerCase()}`;
}

const maritalStatusLabels: Record<
  string,
  { masculine: string; feminine: string }
> = {
  SOLTEIRO: { masculine: "Solteiro", feminine: "Solteira" },
  CASADO: { masculine: "Casado", feminine: "Casada" },
  SEPARADO: { masculine: "Separado", feminine: "Separada" },
  DIVORCIADO: { masculine: "Divorciado", feminine: "Divorciada" },
  VIUVO: { masculine: "Viúvo", feminine: "Viúva" }
};

export function getFirstAndLastName(fullName?: string) {
  if (!fullName) return "";

  const names = fullName.trim().split(/\s+/).filter(Boolean);
  if (names.length <= 1) return names[0] ?? "";

  return `${names[0]} ${names[names.length - 1]}`;
}

export function getMaritalStatusLabel(maritalStatus?: string, sex?: string) {
  if (!maritalStatus) return "";

  const labels = maritalStatusLabels[maritalStatus.toUpperCase()];
  if (!labels) return capitalizeFirstLetter(maritalStatus);

  return sex?.toUpperCase() === "FEMININO" ? labels.feminine : labels.masculine;
}

export function getAgeByDateOfBirth(dateOfBirth: string) {
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  const isBirthdayPassedThisYear =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate());

  return isBirthdayPassedThisYear ? age : age - 1;
}

export function getInitials(fullName: string): string {
  if (!fullName) return "";

  const names = fullName.trim().split(" ");

  if (names.length === 1) {
    return names[0][0].toUpperCase();
  }

  const firstNameInitial = names[0][0].toUpperCase();
  const lastNameInitial = names[names.length - 1][0].toUpperCase();

  return `${firstNameInitial}${lastNameInitial}`;
}

export const findValueInObject = (fieldName: string, values: any) => {
  const parts = fieldName.split(".");
  let value = values;

  for (const part of parts) {
    value = value?.[part];
  }

  return value;
};

// formata enquanto o numero é digitado no input
export const decimalFormatterInput = (value: string | number) => {
  let newValue = String(value).replace(/\D/g, "");

  newValue = (Number(newValue) / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return newValue;
};
// recebe um valor e retorna ele formatado
export const decimalFormatter = (value: string | number) => {
  let newValue = String(value).replace(/[^0-9.]/g, "");

  newValue = parseFloat(newValue).toFixed(2);

  return Number(newValue).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const parseFormattedDecimal = (formattedValue: string): number => {
  if (!formattedValue) return 0;

  const numericValue = formattedValue.replace(/\./g, "").replace(",", ".");

  return parseFloat(numericValue) || 0;
};

export function isDev(): boolean {
  return process.env.NODE_ENV === "development";
}

export type RelativeTimeTone = "accent" | "neutral" | "danger";

export function getRelativeTime(date: string | Date): {
  label: string;
  tone: RelativeTimeTone;
} {
  const target = typeof date === "string" ? parseISO(date) : new Date(date);

  if (isNaN(target.getTime())) return { label: "", tone: "neutral" };

  const now = new Date();
  const diffMin = Math.round((target.getTime() - now.getTime()) / 60000);

  if (diffMin < -1) return { label: "Atrasado", tone: "danger" };
  if (diffMin <= 1) return { label: "Agora", tone: "accent" };
  if (diffMin < 60) return { label: `Em ${diffMin} min`, tone: "accent" };

  const sameDay = target.toDateString() === now.toDateString();
  if (sameDay) {
    const hours = Math.floor(diffMin / 60);
    const minutes = diffMin % 60;
    const rest = minutes
      ? `${hours}h${String(minutes).padStart(2, "0")}`
      : `${hours}h`;
    return { label: `Em ${rest}`, tone: "neutral" };
  }

  return { label: formatDate(target, "dd/MM 'às' HH'h'mm"), tone: "neutral" };
}

export function getScheduleDateTimeLabel(date: string | Date): string {
  const target = typeof date === "string" ? parseISO(date) : new Date(date);

  if (isNaN(target.getTime())) return "";

  const sameDay = target.toDateString() === new Date().toDateString();

  return sameDay
    ? formatDate(target, "'Hoje às' HH'h'mm")
    : formatDate(target, "dd/MM 'às' HH'h'mm");
}

export function formatDate(
  date: string | number | Date,
  formatString: string,
  options?: {
    locale?: Locale;
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    firstWeekContainsDate?: FirstWeekContainsDate;
    useAdditionalWeekYearTokens?: boolean;
    useAdditionalDayOfYearTokens?: boolean;
  }
): string {
  try {
    const parsedDate =
      typeof date === "string" ? parseISO(date) : new Date(date);

    if (isNaN(parsedDate.getTime())) {
      throw new Error("Invalid date");
    }

    return format(parsedDate, formatString, options);
  } catch (error) {
    console.error("Error formatting date:", { date, formatString, error });
    return "";
  }
}

export const formatBRL = (value: string | number) => {
  if (value === null || value === undefined || value === "") return "R$ 0,00";
  return `R$ ${decimalFormatter(value)}`;
};
